// REQ-102 — Piezas compartidas del muro de post-its: etiqueta de origen, formato de fecha,
// etiquetas con logo ("Recibido en") y reacciones (FASE 4, paridad demo H-19/H-50/H-51).
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecognitionOrigin, CampaignDto, RecognitionDto, FundamentalDto, ReactionUserDto } from '@/api/recognition';
import { useReactions, useReactionUsers, useToggleReaction, getRecognitionErrorMessage, getFundamentalName } from '@/api/recognition';
import { FloatingTooltip } from '@/components/common/FloatingTooltip';
import { REACTION_EMOJIS, pickTopReaction } from '@/utils/reactions';
import { getInitials } from '@/utils/strings';
import { avatarColorByIndex, avatarColorForSeed, avatarVars } from '@/utils/avatarPalette';
import { normalizeHex, getFundamentalIcon } from './fundamentalAssets';
import { shineLogoSrc } from './shineLogo';
import { fundamentalsStackedLogoSrc } from './fundamentalsStackedLogo';

/**
 * Colores pastel del "papel" del post-it (fondo). Un mismo Fundamental cae siempre en el mismo
 * tono, tanto en la tarjeta del muro (`PostItWall`) como en el detalle ampliado
 * (`PostItDetailModal`) — el texto del post-it (`.postit-text` y familia) es oscuro y fijo
 * (`recognition.css`), pensado para leerse sobre estos tonos claros. Usar en su lugar el color
 * VIBRANTE del Fundamental (`getFundamentalColor`, pensado para chips/pines/íconos, no para fondos
 * de texto) rompe el contraste — el fondo puede quedar tan oscuro como el propio texto.
 *
 * Diseñada 1:1 con `FUNDAMENTAL_PALETTE` (naranja, morado, rojo, verde, azul, rosa) — cada entrada
 * es la versión clara del color en esa misma posición. Usar `getPostItPaperColor` para resolverla:
 * el índice por sí solo NO es fiable (ver ese comentario).
 */
export const POSTIT_PAPER_COLORS = ['#FDE8CC', '#D8DDFB', '#FBDADE', '#D4F5E0', '#D9F0FB', '#FBE4F1'];

/**
 * BUG-01 (post-its del muro con color equivocado) — pastel por COLOR real del Fundamental
 * (`f.color`), no por posición en el catálogo. Claves = hex normalizado, mismo criterio que
 * `FUNDAMENTAL_ICON_BY_COLOR` en `fundamentalAssets.ts`.
 *
 * Antes, tanto `PostItWall.resolvePaper` como `PostItDetailModal` indexaban directo en
 * `POSTIT_PAPER_COLORS[funds.indexOf(fundamental) % ...]`: si el catálogo llegaba del backend en
 * un orden distinto al que asume `FUNDAMENTAL_PALETTE` (naranja, morado, rojo, verde, azul, rosa —
 * p. ej. porque el admin reordenó los Fundamentales, o el backend no garantiza ese orden), un
 * Fundamental verde en la posición 0 se pintaba con el pastel de "naranja". El chip del modal de
 * detalle (`getFundamentalColor`, que sí mira `f.color` antes que el índice) mostraba el verde
 * correcto — de ahí que el color pareciera "correcto solo al abrir el post-it".
 */
const POSTIT_PAPER_BY_COLOR: Record<string, string> = {
  '#f08521': POSTIT_PAPER_COLORS[0],
  '#e08a3e': POSTIT_PAPER_COLORS[0],
  '#342f82': POSTIT_PAPER_COLORS[1],
  '#332f81': POSTIT_PAPER_COLORS[1],
  '#3d3d8e': POSTIT_PAPER_COLORS[1],
  '#e11f26': POSTIT_PAPER_COLORS[2],
  '#d32f2f': POSTIT_PAPER_COLORS[2],
  '#20b15a': POSTIT_PAPER_COLORS[3],
  '#4caf50': POSTIT_PAPER_COLORS[3],
  '#40b8e9': POSTIT_PAPER_COLORS[4],
  '#ef5395': POSTIT_PAPER_COLORS[5],
};

/**
 * Pastel del papel de un post-it para un Fundamental: por su color real si se reconoce: si no
 * (Fundamental sin `color`, o un tono fuera de la paleta de marca), por índice del catálogo, igual
 * que antes. Mismo criterio que `getFundamentalIcon` (`fundamentalAssets.ts`): color primero,
 * índice solo como último recurso.
 */
export function getPostItPaperColor(color: string | null | undefined, index: number): string {
  const hex = normalizeHex(color);
  const byColor = hex ? POSTIT_PAPER_BY_COLOR[hex] : undefined;
  if (byColor) return byColor;
  return POSTIT_PAPER_COLORS[index % POSTIT_PAPER_COLORS.length];
}

/**
 * REQ-107 — Configuración de acciones de un post-it. Las acciones aparecen SOLO si la config
 * lo permite (toggles de OU/campaña de la sección "Gestión" del admin — backend D5-D6). Hasta que
 * exista ese endpoint se usan defaults alineados a la maqueta: Reportar ON, Edición post-envío OFF,
 * eliminar propio ON. Si la campaña trae overrides en `diseno` (JSON libre) se respetan.
 */
export interface PostItActionConfig {
  canEdit: boolean;
  canDelete: boolean;
}

/** Defaults alineados a la maqueta (Gestión → Moderación de envío). */
export const DEFAULT_ACTION_CONFIG: PostItActionConfig = {
  canEdit: false,
  canDelete: true,
};

/** Variante de DEFAULT_ACTION_CONFIG que respeta GestionSettingsDto.AllowPostEdit cuando no hay
 *  campaña (ni por tanto `campaign.diseno`) de por medio. */
export function resolveDefaultActionConfig(allowPostEdit: boolean): PostItActionConfig {
  return { canEdit: allowPostEdit, canDelete: DEFAULT_ACTION_CONFIG.canDelete };
}

/**
 * T1 / RF-107 — ¿Sigue dentro de plazo para editarse este reconocimiento concreto?
 *
 * 🔴 Esta pregunta es POR POST-IT, no por muro, y ahí está la diferencia con `canEdit`: la respuesta
 * depende de la fecha de cada uno. Un muro cualquiera mezcla lo de esta mañana con lo del año pasado,
 * y un único «se puede editar» para todos enseñaría el lápiz sobre reconocimientos que el servidor va
 * a rechazar con 403.
 *
 * `null`/ausente ⇒ sin límite de tiempo. No es «no se puede»: si se puede o no lo dice `canEdit`.
 *
 * ⚠️ Esto es PRESENTACIÓN. Quien decide es `UpdateRecognitionCommandHandler`, que compara contra el
 * reloj del servidor. Aquí se compara con el del navegador, que puede ir desfasado — en el peor caso
 * se esconde un lápiz que aún funcionaba, o se enseña uno que dará 403. Ninguna de las dos cosas
 * abre nada: el plazo no es un permiso, es una caducidad sobre algo que `canEdit` ya autorizó.
 */
export function dentroDePlazoDeEdicion(
  fechaIso: string,
  ventanaHoras: number | null | undefined,
  ahora: Date = new Date(),
): boolean {
  if (ventanaHoras === null || ventanaHoras === undefined) return true;
  const enviado = new Date(fechaIso).getTime();
  // Una fecha que no se sabe leer no puede esconder el lápiz: el servidor tiene el dato bueno y es
  // quien decide. Esconderlo aquí dejaría al emisor sin forma de intentarlo siquiera.
  if (Number.isNaN(enviado)) return true;
  // El límite es INCLUSIVO, igual que en el backend: justo al cumplirse el plazo todavía se puede.
  return ahora.getTime() - enviado <= ventanaHoras * 3_600_000;
}

/**
 * REQ-107 — ¿el mensaje editado cumple el mínimo de caracteres configurado por la OU
 * (`RecognitionRulesDto.minCommentChars`)? Mismo criterio que exige `Recognition.Edit` en el
 * backend (`RecognitionDomainException` si el mensaje trimeado queda por debajo del mínimo) y que ya
 * usa el formulario de ENVÍO (`ReconocerPanel.messageOk`) — la edición inline y la del modal de
 * detalle no lo comprobaban, así que el botón de guardar quedaba activo con un mensaje que el
 * servidor iba a rechazar, y sin ningún indicador visible mientras se escribía.
 */
export function isEditedMessageValid(message: string, minCommentChars: number): boolean {
  const trimmed = message.trim();
  return trimmed.length > 0 && trimmed.length >= Math.max(minCommentChars, 0);
}

/**
 * Resuelve la config de acciones desde la campaña activa (override en `diseno`) o los defaults.
 *
 * RF-084 — "Reportar" YA NO sale de aquí. La campaña describe el diseño de una convocatoria; que se
 * pueda reportar o no es una decisión de moderación por geografía (Site → BU → OU → global) que vive
 * en `ModerationSettings` y llega ya resuelta por el servidor en `RecognitionDto.reportEnabled`.
 * Mientras el botón dependía de `campaign.diseno.allowReport`, apagar los reportes en una OU no lo
 * ocultaba, y el usuario recibía un 403 al enviarlo.
 */
export function resolveActionConfig(campaign: CampaignDto | null | undefined): PostItActionConfig {
  const raw = campaign?.diseno as Record<string, unknown> | null | undefined;
  if (!raw) return DEFAULT_ACTION_CONFIG;
  const bool = (k: string, d: boolean) => (typeof raw[k] === 'boolean' ? (raw[k] as boolean) : d);
  return {
    canEdit: bool('allowEdit', DEFAULT_ACTION_CONFIG.canEdit),
    canDelete: bool('allowDelete', DEFAULT_ACTION_CONFIG.canDelete),
  };
}

/**
 * ¿Se ofrece el botón "Reportar" para este reconocimiento? El valor efectivo lo resuelve el backend
 * por la geografía del contenido; un DTO anterior a RF-084 no lo trae y se asume permitido (el
 * backend revalida al crear el reporte, regla de oro #3).
 */
export function canReportRecognition(recognition: Pick<RecognitionDto, 'reportEnabled'>): boolean {
  return recognition.reportEnabled !== false;
}

const ORIGIN_META: Record<RecognitionOrigin, { cls: string; icon: string; key: string }> = {
  Diario:        { cls: 'rec-origin--diario',    icon: 'fa-pen',          key: 'origin.diario' },
  Brillamos:     { cls: 'rec-origin--brillamos', icon: 'fa-star',         key: 'origin.brillamos' },
  Personalizada: { cls: 'rec-origin--brillamos', icon: 'fa-bullhorn',     key: 'origin.personalizada' },
  Csv:           { cls: 'rec-origin--sistema',   icon: 'fa-file-csv',     key: 'origin.csv' },
  Delegado:      { cls: 'rec-origin--delegado',  icon: 'fa-user-pen',     key: 'origin.delegado' },
  Sistema:       { cls: 'rec-origin--sistema',   icon: 'fa-building',     key: 'origin.sistema' },
};

/** Etiqueta de origen de un reconocimiento (Diario / Brillamos / Sistema / Delegado / …). */
export function OriginTag({ origen }: { origen: RecognitionOrigin }) {
  const { t } = useTranslation('recognition');
  const meta = ORIGIN_META[origen] ?? { cls: 'rec-origin--otro', icon: 'fa-tag', key: 'origin.diario' };
  return (
    <span className={`rec-origin-tag ${meta.cls}`}>
      <i className={`fas ${meta.icon}`} /> {t(meta.key)}
    </span>
  );
}

/** Formatea una fecha ISO en el idioma de la UI; si no es válida, la devuelve tal cual. */
export function formatDate(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/**
 * Avatar de persona: foto real de AD/Entra ID cuando existe (`photoUrl`, sincronizada desde Graph
 * por UserAdminService — PENDIENTE de backend, ver PENDIENTES.md), con caída a las iniciales de
 * siempre si no hay foto o si la URL falla al cargar. `baseClass` es la clase BASE del avatar
 * (`rp2-avatar`, `rp2-sr-av`, …): ya trae `object-fit: cover` para la `<img>`; el modificador
 * `--initials` solo se agrega en el fallback.
 */
export function PersonAvatar({ name, photoUrl, baseClass, seed, colorIndex }: Readonly<{
  name: string;
  /**
   * La imagen a pintar, ya resuelta. Puede ser la foto de Entra o la URL del avatar elegido
   * (RF-008) — este componente **no decide cuál**: la precedencia se aplica en quien lo monta.
   *
   * 🔴 Y por eso este componente sigue siendo PURO, sin hooks de datos. Se intentó resolver el
   * avatar aquí dentro y fue un error: `PersonAvatar` se pinta dentro de `.map()` en media docena de
   * pantallas, así que meterle `useAvatarUrl` le añade una dependencia de `QueryClientProvider` a
   * cada sitio que lo use — reventaron 11 pruebas que lo montaban sin proveedor, y en tiempo de
   * ejecución sería la misma fragilidad. Quien pinta una lista resuelve con
   * `useAvatarUrlResolver()` UNA vez y pasa la URL ya hecha.
   */
  photoUrl?: string | null;
  baseClass: string;
  /** Semilla estable del color de fallback (paleta corporativa). Sin ella se usa el propio `name`. */
  seed?: string | null;
  /** Posición en la lista, cuando la hay: el color sigue la secuencia de la paleta. */
  colorIndex?: number;
}>) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [photoUrl]);

  if (photoUrl && !failed) {
    return <img src={photoUrl} alt="" className={baseClass} onError={() => setFailed(true)} />;
  }

  const fondo = colorIndex === undefined ? avatarColorForSeed(seed ?? name) : avatarColorByIndex(colorIndex);
  return (
    <div className={`pmp-avatar ${baseClass} ${baseClass}--initials`} style={avatarVars(fondo)}>
      {getInitials(name)}
    </div>
  );
}

/**
 * Ícono de Fundamental de un post-it/carpeta (`.postit-fund-icon`, `.rp2-folder-item-fund-icon`).
 *
 * 🔴 Antes cada pantalla (`PostItWall`, `ProfileFolderPanel`) tomaba el PRIMER `fundamentalIds`
 * que existiera en el catálogo y pintaba solo ese ícono. Con un reconocimiento multi-fundamental
 * eso confundía: el ícono mostrado no siempre coincidía con la carpeta/campaña que el usuario
 * estaba mirando, y no había forma de saber por qué — los demás Fundamentales aplicados quedaban
 * invisibles. Con más de uno resuelto, el ícono se envuelve en `FloatingTooltip` (hover/foco) que
 * lista TODOS los aplicados; con uno solo se deja igual que antes (nada que aclarar).
 *
 * Vive aquí, compartido, para que el arreglo no se haga en una pantalla y se quede roto en la otra.
 */
export function RecognitionFundIcon({
  recognition, catalog, lang, iconClassName,
}: Readonly<{
  recognition: Pick<RecognitionDto, 'fundamentalIds'>;
  catalog: FundamentalDto[];
  lang: string;
  iconClassName: string;
}>) {
  const { t } = useTranslation('recognition');
  const funds = recognition.fundamentalIds
    .map((id) => catalog.find((fund) => fund.id === id))
    .filter((fund): fund is FundamentalDto => Boolean(fund));

  if (funds.length === 0) return null;

  const primary = funds[0];
  const primaryIcon = getFundamentalIcon(primary, catalog.indexOf(primary));

  if (funds.length === 1) {
    return <img src={primaryIcon} alt="" className={iconClassName} />;
  }

  return (
    <FloatingTooltip
      tooltipClassName="adm-vista-tooltip"
      content={(
        <div className="fund-tooltip">
          <p className="fund-tooltip-title">{t('wall.fundamentalsRecognized')}</p>
          <div className="fund-tooltip-list">
            {funds.map((fund) => (
              <span key={fund.id} className="fund-tooltip-item">
                <img src={getFundamentalIcon(fund, catalog.indexOf(fund))} alt="" className="fund-tooltip-icon" />
                {getFundamentalName(fund, lang)}
              </span>
            ))}
          </div>
        </div>
      )}
    >
      {(anchorProps) => (
        <button type="button" className={iconClassName} aria-label={t('wall.fundIconTooltipAria')} {...anchorProps}>
          <img src={primaryIcon} alt="" />
        </button>
      )}
    </FloatingTooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FASE 4 (paridad demo, H-51) — Etiquetas "Recibido en" con logo, en vez de la etiqueta de texto
// `OriginTag`. El demo (`HomePage.tsx` : 167-180) solo conoce dos programas: Fundamentales
// (siempre, `/fundamentals-v2.svg`) y Brillamos (`/brillamos/logo-shine-color.svg`, cuando el
// reconocimiento fue promovido/pertenece a esa campaña). El integrado tiene más orígenes sin
// equivalente en el demo (Csv/Sistema/Delegado) — para esos casos ESTE componente cae de vuelta
// a `OriginTag` (texto, restilada) en vez de perder la distinción (regla 6: restilar, no borrar).
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ¿Este reconocimiento pertenece al programa Brillamos Juntos (campaña Brillamos/Personalizada, o
 * promovido desde un diario con `campanaSecundariaId`)? Compartido por el tag "Recibido en" y por
 * el pin morado con ⭐ del muro (`PostItWall.tsx`) — mismo criterio en los dos lugares.
 *
 * 🔴 WP-B3 — que `Personalizada` cuente como Brillamos aquí PARECE el bug de «el logo de Shine en
 * campañas que no son Shine», y se midió antes de tocarlo: no lo es, y cambiarlo no arreglaría nada.
 * Desde RF-076/RN-C12 el backend deja fuera las personalizadas de toda consulta sin `campaignId`
 * (`GetFeedQueryHandler`), así que no llegan ni al muro ni al perfil; y su propio landing
 * (`InnovacionView`) pinta una lista simple, sin pin ni tags. No queda ningún sitio donde se vean
 * con identidad de Shine.
 *
 * Separarlas aquí, además, rompería la sincronía DECLARADA con `RecognitionProgramResolver` del
 * backend, que responde a la misma pregunta —a qué PROGRAMA pertenece— para el filtro `programa`.
 * Esa pregunta y «qué identidad visual muestro» coinciden hoy; si algún día dejan de coincidir, lo
 * que hay que añadir es una función nueva, no torcer esta.
 */
export function isBrillamosOrigin(recognition: Pick<RecognitionDto, 'origen' | 'campanaSecundariaId'>): boolean {
  return recognition.origen === 'Brillamos' || recognition.origen === 'Personalizada' || Boolean(recognition.campanaSecundariaId);
}

/**
 * WP-E2 · RN-V01 — Nombre a mostrar del emisor, con sus TRES casos bien separados.
 *
 * 🔴 Hasta este paquete las cinco pantallas que pintan un emisor repetían
 * `anonimo || !emisorNombre ? t('wall.anonymous') : emisorNombre`, y eso llamaba «Anónimo» a dos
 * cosas distintas:
 *   1. El emisor ELIGIÓ el anonimato (`anonimo`) — «Anónimo» es correcto.
 *   2. Una REGLA tapó su identidad sin que él lo pidiera: RN-V01 (un visor de fuera de Europa
 *      mirando un reconocimiento de OU europea) o una excepción de visibilidad por localidad
 *      (CPE-138). Aquí «Anónimo» le atribuye una decisión que no tomó.
 *
 * El orden importa: `anonimo` se comprueba PRIMERO, porque cuando el emisor eligió el anonimato eso
 * es lo que la pantalla debe decir aunque además aplique RN-V01.
 *
 * Vive aquí y no en cada pantalla para que la distinción no se arregle en una y se quede rota en
 * las otras cuatro.
 */
export function nombreDelEmisor(
  recognition: Pick<RecognitionDto, 'anonimo' | 'emisorNombre'>,
  t: (key: string) => string,
): string {
  if (recognition.anonimo) return t('wall.anonymous');
  return recognition.emisorNombre || t('wall.senderHidden');
}

/**
 * WP-A5 · RN-P09 — el valor en puntos de ESTA nominación, cuando el servidor lo deja ver.
 *
 * 🔴 `!= null`, NUNCA la veracidad del número. `0` es un valor real —una OU puede tener sus
 * criterios activos configurados en cero— y `null` es «no procede o no se puede ver». Un
 * `{puntos && ...}` escondería el cero legítimo, que es exactamente la confusión que el contrato
 * evita declarando el campo nullable.
 *
 * 🔴 Y aquí NO se decide nada. El servidor lo resuelve contra cinco puertas —config de puntos de la
 * OU, toggle de la campaña, que no sea diario, el interruptor «Visible para empleados» y el suelo
 * duro de GDPR— y lo manda ya resuelto. Si viene, se enseña; si no, no. Deducirlo en el cliente es
 * lo que la regla de oro prohíbe.
 *
 * Vive aquí, y no en cada pantalla, por lo mismo que `nombreDelEmisor`: el post-it se dibuja desde
 * varios sitios y una regla repartida acaba arreglándose en uno y quedándose rota en los otros.
 *
 * ⚠️ **Es el ACUMULADO lo que nunca se enseña** (RN-P12), no esto. Si alguna vez alguien quiere
 * sumar estos valores para pintar un total, ese total está prohibido — ni siquiera a su titular.
 */
/**
 * WP-F5 — Texto de ayuda del campo de mensaje: el configurado en Gestión para el idioma activo, o el
 * del producto si no hay ninguno.
 *
 * 🔴 El respaldo NO es opcional y por eso vive aquí y no en cada pantalla: un placeholder en blanco
 * quita la única pista de qué escribir, así que «configurado pero vacío» tiene que comportarse como
 * «no configurado». Se comprueba con `trim()` porque un texto de solo espacios se guarda igual de
 * fácil que uno vacío y se ve idéntico.
 *
 * Recibe el diccionario sin resolver —igual que el banner— para que cambiar de idioma sin recargar
 * cambie también este texto.
 */
/**
 * WP-F5 — Los Fundamentales que una puerta de envío puede ofrecer.
 *
 * 🔴 Son DOS filtros distintos y hay que aplicar los dos:
 *   · `isActive` es el borrado lógico del CATÁLOGO, que administra Pulso (D-27);
 *   · `retirados` es lo que ESTE programa quitó (bloque «Fundamentales disponibles» de Gestión),
 *     y llega por `GET v1/recognitions/rules` porque el documento de Gestión es AdminGlobal-only.
 *
 * 🔴 Y vive aquí, compartido, en vez de repetirse en cada panel: las dos puertas de envío tienen que
 * ofrecer EXACTAMENTE lo mismo. Con una copia por panel, retirar un Fundamental lo escondería en una
 * y no en la otra el día que una de las dos se tocara. Además, una prueba que reimplemente este
 * filtro no verifica nada (R2) — así hay una única definición que llamar.
 *
 * Ofrecer uno retirado no es cosmético: el servidor rechaza el envío con 422.
 */
/**
 * MIME propios (no estándar) usados para arrastrar una palabra del banco (`WordBankTabs`) y
 * soltarla sobre su slot correspondiente del post-it (`PostItCard`/`TemplateSlot`). Uno por
 * categoría, en vez de un único MIME con un payload JSON, porque `DataTransfer.types` (a
 * diferencia de `getData`) SÍ es legible durante `dragover` — permite que cada slot sepa, antes
 * de soltar, si lo que se arrastra es de su propia categoría (y así aceptar/rechazar el drop con
 * `preventDefault`) sin depender de leer datos que el navegador solo entrega en el `drop`.
 */
export const DRAG_MIME = {
  fundamental: 'application/x-ri-fundamental',
  behavior: 'application/x-ri-behavior',
  habit: 'application/x-ri-habit',
  impact: 'application/x-ri-impact',
} as const;

export function fundamentalesOfrecibles<T extends { id: string; isActive: boolean }>(
  catalogo: readonly T[] | undefined,
  retirados: readonly string[] | null | undefined,
): T[] {
  const fuera = retirados ?? [];
  return (catalogo ?? []).filter((f) => f.isActive && !fuera.includes(f.id));
}

export function placeholderDelMensaje(
  configurado: Record<string, string> | null | undefined,
  idioma: string,
  respaldo: string,
): string {
  const texto = configurado?.[idioma] ?? configurado?.[idioma.split('-')[0]];
  return texto && texto.trim() ? texto : respaldo;
}

export function PuntosDeLaNominacion({ recognition }: Readonly<{
  recognition: Pick<RecognitionDto, 'puntosDeEstaNominacion'>;
}>) {
  const { t } = useTranslation('recognition');
  const puntos = recognition.puntosDeEstaNominacion;

  if (puntos == null) return null;

  return (
    <span className="postit-puntos" title={t('wall.pointsTitle')}>
      {t('wall.points', { count: puntos })}
    </span>
  );
}

export function PostItTagsArea({ recognition }: { recognition: Pick<RecognitionDto, 'origen' | 'fundamentalIds' | 'campanaSecundariaId' | 'mostrarEtiquetaCampana'> }) {
  const { t, i18n } = useTranslation('recognition');
  // B8-3 — «Mostrar etiqueta de campaña» (Paso 5). El servidor manda la decisión ya tomada; aquí no
  // se recalcula. Un DTO anterior a este cambio no trae el campo: se asume que sí, como el resto de
  // flags aditivos del muro.
  if (recognition.mostrarEtiquetaCampana === false) return null;
  const showBrillamos = isBrillamosOrigin(recognition);
  // Enviado directo desde Brillamos (origen Brillamos/Personalizada): aunque el reconocimiento
  // traiga `fundamentalIds` (se elige un Fundamental dentro del propio flujo de Brillamos), NO es
  // un envío de Fundamentales — solo se muestra el logo de Shine. El logo de Fundamentales queda
  // solo para el envío promovido (origen Diario + `campanaSecundariaId`), donde sí salen ambos.
  const isDirectBrillamosSend = recognition.origen === 'Brillamos' || recognition.origen === 'Personalizada';
  const showFundamentales = recognition.fundamentalIds.length > 0 && !isDirectBrillamosSend;
  const needsFallback = recognition.origen === 'Csv' || recognition.origen === 'Sistema' || recognition.origen === 'Delegado';

  if (!showBrillamos && !showFundamentales && !needsFallback) return null;

  return (
    <div className="postit-tags-area">
      <span className="postit-tags-label">{t('wall.receivedIn')}</span>
      <div className="postit-tags">
        {showFundamentales && (
          <span className="postit-tag postit-tag--fund">
            {/* QA (2026-09-01) — DT-029: mismo defecto que el de Brillamos abajo, pero sin notar hasta
                ahora: `/fundamentals-v2.svg` es la palabra "FUNDAMENTALS" en INGLÉS dibujada a mano,
                sin variante por idioma. `fundamentalsStackedLogoSrc` cae al arte apilado nuevo en
                español y al wordmark ancho en los demás idiomas (ver el comentario del helper). */}
            <img src={fundamentalsStackedLogoSrc(i18n.language)} alt="Fundamentales" className="postit-tag-logo" />
          </span>
        )}
        {showBrillamos && (
          <span className="postit-tag postit-tag--brillamos">
            {/* QA (2026-08-28) — DT-029: logo fijo en inglés/genérico, sin importar el idioma
                activo. `shineLogoSrc` ya resuelve el wordmark por idioma (mismo helper que
                `BrillamosView.tsx`); solo faltaba usarlo aquí. */}
            <img src={shineLogoSrc(i18n.language, 'color')} alt="Shine" className="postit-tag-logo" />
          </span>
        )}
        {needsFallback && <OriginTag origen={recognition.origen} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reacciones del post-it — ver docs/superpowers/specs/2026-08-06-reacciones-postit-design.md.
// Reemplaza el maquetado efímero anterior (`PostItReactions`, estado local sin persistencia):
// ahora persiste vía `useReactions`/`useToggleReaction` (TanStack Query, optimistic update) contra
// el backend real (colección `recognition_reactions`, ver docs/contexto/decisiones.md). En local
// sigue disponible el mock in-repo (`VITE_RECOGNITION_MOCK=true`) para desarrollo sin backend.
// `RECOGNITION_FEATURES.reactions` está ON en todos los ambientes.
// ─────────────────────────────────────────────────────────────────────────────
export const RECOGNITION_FEATURES = {
  /** Backend real desplegado (ver docs/superpowers/plans/2026-08-06-reacciones-postit.md) — ON en todos los ambientes. */
  reactions: true,
};

/** ¿Debe mostrarse la UI de reacciones? Sí en mock (desarrollo/QA) o cuando el flag esté ON. */
export function shouldShowReactions(): boolean {
  return RECOGNITION_FEATURES.reactions || import.meta.env.VITE_RECOGNITION_MOCK === 'true';
}

/**
 * Badge de reacciones de un post-it: emoji más frecuente + contador (solo si `total > 1`), o un
 * ícono neutro si todavía no tiene ninguna. El clic abre un picker con los 5 emojis curados;
 * elegir uno hace toggle (mismo emoji la quita, otro la reemplaza — reacción única por usuario).
 */
export function PostItReactionBadge({
  recognitionId, reactionsEnabled = true,
}: { recognitionId: string; reactionsEnabled?: boolean }) {
  const { t } = useTranslation('recognition');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data } = useReactions(recognitionId);
  // Lista de quiénes reaccionaron: LAZY — solo se resuelve cuando el popover está abierto, no por
  // cada tarjeta del muro.
  const reactors = useReactionUsers(recognitionId, open);
  const toggle = useToggleReaction();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!reactionsEnabled || !shouldShowReactions()) return null;

  const counts = data?.counts ?? {};
  const total = data?.total ?? 0;
  const myReaction = data?.myReaction ?? null;
  const topEmoji = pickTopReaction(counts);

  const handleSelect = (emoji: string) => {
    toggle.mutate({ id: recognitionId, emoji });
    setOpen(false);
  };

  return (
    <div className="postit-reaction-badge-wrap" onClick={(evt) => evt.stopPropagation()} ref={containerRef}>
      <button
        type="button"
        className="postit-reaction-badge"
        aria-label={total > 0 ? t('reactions.countLabel', { count: total }) : t('reactions.addLabel')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {topEmoji ? (
          <span className="postit-reaction-badge-emoji">{topEmoji}</span>
        ) : (
          <i className="far fa-smile" aria-hidden="true" />
        )}
        {total > 1 && <span className="postit-reaction-badge-count">{total}</span>}
      </button>

      {open && (
        <div className="postit-reaction-popover" role="menu">
          <div className="postit-reaction-picker">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                role="menuitem"
                className={`postit-reaction-picker-item ${myReaction === emoji ? 'is-selected' : ''}`}
                aria-label={emoji}
                aria-pressed={myReaction === emoji}
                onClick={() => handleSelect(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>

          {total > 0 && (
            <div className="postit-reaction-who">
              <p className="postit-reaction-who-title">{t('reactions.whoReacted')}</p>
              {reactors.isLoading && (
                <p className="postit-reaction-who-status">{t('reactions.whoLoading')}</p>
              )}
              {reactors.isError && (
                <p className="postit-reaction-who-status" role="alert">{t('reactions.whoError')}</p>
              )}
              {reactors.data && (
                reactors.data.users.length === 0 ? (
                  <p className="postit-reaction-who-status">{t('reactions.whoEmpty')}</p>
                ) : (
                  <ul className="postit-reaction-who-list">
                    {reactors.data.users.map((u: ReactionUserDto) => (
                      <li key={u.userId} className="postit-reaction-who-item">
                        <span className="postit-reaction-who-emoji" aria-hidden="true">{u.emoji}</span>
                        <span className="postit-reaction-who-name">{u.displayName}</span>
                      </li>
                    ))}
                  </ul>
                )
              )}
            </div>
          )}
        </div>
      )}

      {toggle.isError && (
        <p className="rp-error postit-reaction-error" role="alert">
          {getRecognitionErrorMessage(toggle.error, t('reactions.error'))}
        </p>
      )}
    </div>
  );
}
